import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { isNorthwesternMutualBrokerDealer } from '../../utils';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { invalidateAccountAdminQuery, useAdminAccountQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import {
	PartialAccountSaveError,
	useUpdateAdminAccountMutation,
} from '../../../queries/Admin/Account/useUpdateAdminAccountMutation';
import {
	requiresSpecialistChangeJustification,
	useUpdateCustomerSuccessSpecialistMutation,
} from '../../../queries/Admin/Account/useUpdateCustomerSuccessSpecialistMutation';
import { useUpdateManagementAccountIdMutation } from '../../../queries/Admin/Account/useUpdateManagementAccountIdMutation';
import { useUpdateNextMeetingMutation } from '../../../queries/Admin/Account/useUpdateNextMeetingMutation';
import { Skeleton } from '../../../web/components-v2/Skeleton';
import { AccountInsightsBanner } from '../../components/accountDetails/AccountInsightsBanner';
import { InternalOnly } from '../../components/InternalOnly';
import { accountDetailsTabContentOverviewStyles as styles } from './accountDetailsTabContentOverviewStyles';
import { AccountNotesSection } from './overview/AccountNotesSection';
import { BillingCard } from './overview/BillingCard';
import { CustomizationsCard } from './overview/CustomizationsCard';
import { GeneralInformationCard } from './overview/GeneralInformationCard';
import { SpecialistChangeJustificationDialog } from './overview/SpecialistChangeJustificationDialog';
import type { ICardEditMode } from './overview/useCardEditMode';

export interface IAccountDetailsTabContentOverviewProps {
	onEditModeChange?: (isEditMode: boolean) => void;
	onAccountUpdated?: (account: Api.IAccount) => void;
}

/**
 * The specialist fields are owned by their own endpoint, so the card's edits to them never ride along in
 * the account PUT: the PUT cannot carry a justification and rejects an audited reassignment outright. The
 * payload still carries the pair as the server currently stores it, because the PUT's guard compares
 * against the live account: omitting it is not an option, and sending a stale copy is itself a rejected
 * save.
 */
const SPECIALIST_PATHS = ['additionalInfo.customerSuccessId', 'additionalInfo.customerSuccessName'];

/**
 * Server copy is only shown when it looks like short, user-facing text; anything longer or
 * exception-shaped falls back to a generic message so internal detail can't leak into the dialog.
 */
const specialistChangeErrorMessage = (error: unknown): string => {
	const raw = (error as { systemMessage?: string } | undefined)?.systemMessage?.trim();
	const looksUserFacing = !!raw && raw.length <= 200 && !/\b(at\s+\w+|Exception|stack|null reference)\b/i.test(raw);
	return looksUserFacing ? raw : 'Failed to change the Customer Success specialist. Please try again.';
};

/** Which card, if any, is currently in edit mode. Only one card edits at a time. */
type EditingCard = null | 'general' | 'billing';

/** Immutably set a (possibly dotted) `path` on `target`, cloning each object along the way. */
const setAtPath = (target: Api.IAccount, path: string, value: unknown): Api.IAccount => {
	const segments = path.split('.');
	const root = { ...(target as unknown as Record<string, unknown>) };

	let cursor = root;
	for (let i = 0; i < segments.length - 1; i++) {
		const segment = segments[i];
		const existing = cursor[segment];
		const next = existing && typeof existing === 'object' ? { ...(existing as Record<string, unknown>) } : {};
		cursor[segment] = next;
		cursor = next;
	}
	cursor[segments[segments.length - 1]] = value;

	return root as unknown as Api.IAccount;
};

/** Read a (possibly dotted) `path` off `target`, tolerating missing intermediate objects. */
const getAtPath = (target: Api.IAccount, path: string): unknown =>
	path
		.split('.')
		.reduce<unknown>(
			(cursor, segment) =>
				cursor && typeof cursor === 'object' ? (cursor as Record<string, unknown>)[segment] : undefined,
			target
		);

/**
 * Build the account PUT body by replaying only the fields this card actually edited onto the freshly
 * fetched account.
 *
 * The draft is a shallow clone taken when the card entered edit mode, so every nested object in it
 * still points at pre-edit state. Sending the draft wholesale therefore reverts anything persisted in
 * the meantime by one of the many fields that own their own endpoint — the inline URL/address editors,
 * the will-renew prediction PATCH, Extend Trial, Stop Cancellation, the email-configuration dialog.
 * Replaying just the edited paths makes that structurally impossible, rather than relying on a list of
 * endpoint-owned fields that has to be kept in step with every future in-place editor.
 */
const buildSavePayload = (draft: Api.IAccount, persisted: Api.IAccount, editedPaths: string[]): Api.IAccount =>
	editedPaths.reduce((next, path) => setAtPath(next, path, getAtPath(draft, path)), persisted);

/**
 * Account Overview body. Renders the AI Insights banner (internal-only) plus the
 * General Information and Billing cards. Owns the single shared edit lifecycle: only one
 * card may edit at a time, a shallow draft clone is seeded when a card enters edit, and a
 * card's Save commits the whole draft through the full account PUT mutation.
 *
 * Body only — the page shell owns the nav rail, app bar, account header, and tab rail.
 */
export const AccountDetailsTabContentOverview = ({
	onEditModeChange,
	onAccountUpdated,
}: IAccountDetailsTabContentOverviewProps) => {
	const { accountId } = useParams<{ accountId: string }>();
	const userSession = useAdminUserSession();
	const canModifyAccount = userSession.hasPermission(AdminPermission.ModifyAccount);

	const { data: account, isLoading, isError } = useAdminAccountQuery({ accountId, enabled: !!accountId });
	// A Comp account linked to a master shows a read-only "Main: <company>" link in the Billing card; the
	// account only carries the master's id, so fetch its name here (gated).
	const mainAccountId = account?.mainAccountId;
	const { data: mainAccount } = useAdminAccountQuery({
		accountId: mainAccountId ?? '',
		enabled: !!mainAccountId && account?.planDetails?.billingType === Api.BillingType.Comp,
	});
	// Northwestern Mutual accounts link to a managing account; like `mainAccountName` above, the
	// account only carries the id, so resolve the company name for the read-mode link.
	const managementAccountId = account?.managementAccountId;
	const { data: managementAccount } = useAdminAccountQuery({
		accountId: managementAccountId ?? '',
		enabled: !!managementAccountId,
	});
	const updateMutation = useUpdateAdminAccountMutation();
	const updateNextMeetingMutation = useUpdateNextMeetingMutation();
	const updateManagementAccountIdMutation = useUpdateManagementAccountIdMutation();
	const updateSpecialistMutation = useUpdateCustomerSuccessSpecialistMutation();

	const [editing, setEditing] = useState<EditingCard>(null);
	const [draft, setDraft] = useState<Api.IAccount | undefined>(undefined);
	const [editedPaths, setEditedPaths] = useState<string[]>([]);
	const [justificationDialogOpen, setJustificationDialogOpen] = useState(false);
	const [specialistError, setSpecialistError] = useState<string | null>(null);

	if (isError) {
		return (
			<div className={css(styles.column)}>
				<div className={css(styles.errorState)}>Unable to load this account. Refresh to try again.</div>
			</div>
		);
	}

	if (isLoading || !account) {
		return (
			<div className={css(styles.column)}>
				<Skeleton styles={[styles.skeletonCard]} />
				<Skeleton styles={[styles.skeletonCard]} />
			</div>
		);
	}

	const beginEdit = (card: Exclude<EditingCard, null>) => {
		// Only one card edits at a time — ignore a begin while another card already holds the draft.
		if (editing !== null) {
			return;
		}
		setDraft({ ...(account as unknown as Record<string, unknown>) } as Api.IAccount);
		setEditedPaths([]);
		setEditing(card);
		onEditModeChange?.(true);
	};

	const setDraftField = (path: string, value: unknown) => {
		setDraft(current => (current === undefined ? current : setAtPath(current, path, value)));
		setEditedPaths(current => (current.includes(path) ? current : [...current, path]));
	};

	const cancel = () => {
		setDraft(undefined);
		setEditedPaths([]);
		setEditing(null);
		onEditModeChange?.(false);
	};

	const nextSpecialistId = draft?.additionalInfo?.customerSuccessId ?? '';
	// Reassignment only, never an unassignment: the typeahead cannot produce a blank and the endpoint
	// rejects one, so a blank is not a change this card can send. Gating the PATCH, the PUT filter and
	// the refetch on the same flag is what keeps a blank from being silently dropped by one and acted
	// on by the others.
	const specialistChanged =
		!!nextSpecialistId && (account.additionalInfo?.customerSuccessId ?? '') !== nextSpecialistId;

	const save = async () => {
		if (!draft) {
			return;
		}
		// An audited reassignment needs a justification before anything is persisted, so collect it first
		// and let the dialog drive the commit.
		if (requiresSpecialistChangeJustification(account, nextSpecialistId)) {
			setSpecialistError(null);
			setJustificationDialogOpen(true);
			return;
		}
		await commitSave();
	};

	const commitSave = async (justification?: string) => {
		if (!draft) {
			return;
		}
		// The specialist goes first: it is the only part of this save the server can reject on its
		// content (a missing or over-long justification), and failing before the PUT keeps a rejected
		// change from leaving the rest of the card half-saved. A retry after a later failure is safe
		// because the endpoint no-ops when the specialist already matches, so no second audit email.
		// Its failure is caught here rather than in the main handler below: once it has succeeded, a
		// later failure is an ordinary partial save and must not be reported as a rejected justification.
		//
		// The PUT payload is built on top of the last-read account, which still carries the *old*
		// specialist pair. Once the PATCH below lands, that pair no longer matches the server, so the
		// PUT's audit guard reads the difference as a second, unjustified reassignment and rejects the
		// whole save, surfacing an error for a change that actually persisted. Rebasing the payload onto
		// the account the PATCH returns keeps the pair in step with what is now stored, name included:
		// the API derives the stored name from the user record, so the typeahead's copy of it is not
		// guaranteed to match.
		let persistedAccount = account;

		if (specialistChanged) {
			try {
				const savedWithSpecialist = await updateSpecialistMutation.mutateAsync({
					accountId: account.id,
					userId: nextSpecialistId,
					justification,
				});
				// No body back is not a failure here, so fall back to the draft's pair: its name can in
				// principle differ from the one the API derived, but its id is right, and it beats
				// sending the old pair, which the guard is certain to reject.
				persistedAccount =
					savedWithSpecialist ??
					SPECIALIST_PATHS.reduce((next, path) => setAtPath(next, path, getAtPath(draft, path)), account);
			} catch (error) {
				setSpecialistError(specialistChangeErrorMessage(error));
				if (!justificationDialogOpen) {
					toast.error(specialistChangeErrorMessage(error));
				}
				return;
			}
			// Persisted, so the dialog has done its job. Closing it here means a failure in the rest of
			// the save is reported as the ordinary partial save it is, not as a rejected justification.
			setJustificationDialogOpen(false);
			setSpecialistError(null);
		}

		try {
			// Next meeting is owned by a dedicated endpoint (like the note composers / legacy) — the
			// account PUT doesn't persist additionalInfo.nextMeeting. Compare in UTC (it's a UTC-midnight
			// date) and, when the card changed it to a real date, persist it separately. The edited-path
			// check matters because the read view schedules through that same endpoint in place: without
			// it, a draft seeded before an in-place pick lands would re-send the pre-pick date on save.
			const nextMeetingEdited = editedPaths.includes('additionalInfo.nextMeeting');
			const originalNextMeeting = account.additionalInfo?.nextMeeting
				? moment.utc(account.additionalInfo.nextMeeting).format('YYYY-MM-DD')
				: '';
			const draftNextMeeting = draft.additionalInfo?.nextMeeting
				? moment.utc(draft.additionalInfo.nextMeeting).format('YYYY-MM-DD')
				: '';
			const nextMeetingChanged = nextMeetingEdited && !!draftNextMeeting && draftNextMeeting !== originalNextMeeting;

			// managementAccountId is likewise owned by its own endpoint, and only applies to Northwestern
			// Mutual accounts — legacy clears it for any other broker dealer rather than leaving it stranded.
			const nextManagementAccountId = isNorthwesternMutualBrokerDealer(draft) ? (draft.managementAccountId ?? '') : '';
			const managementAccountIdChanged = (account.managementAccountId ?? '') !== nextManagementAccountId;

			const saved = await updateMutation.mutateAsync({
				account: buildSavePayload(
					draft,
					persistedAccount,
					editedPaths.filter(path => !SPECIALIST_PATHS.includes(path))
				),
			});
			if (managementAccountIdChanged) {
				await updateManagementAccountIdMutation.mutateAsync({
					accountId: account.id,
					managementAccountId: nextManagementAccountId,
				});
			}
			if (nextMeetingChanged) {
				await updateNextMeetingMutation.mutateAsync({ accountId: account.id, nextMeeting: draftNextMeeting });
			}
			if (nextMeetingChanged || managementAccountIdChanged || specialistChanged) {
				// Refetch so the read view reflects the separately-persisted fields.
				invalidateAccountAdminQuery();
			}
			setJustificationDialogOpen(false);
			setSpecialistError(null);
			setDraft(undefined);
			setEditedPaths([]);
			setEditing(null);
			onEditModeChange?.(false);
			if (saved) {
				onAccountUpdated?.(saved);
			}
		} catch (error) {
			// The mutation has no global MutationCache, so failures are surfaced here.
			if (error instanceof PartialAccountSaveError) {
				// The account body already persisted; only the main-account link failed. Leave edit mode
				// so the stale draft can't be re-sent over the saved values, and refetch so the read view
				// shows what is actually live.
				toast.error(error.message);
				invalidateAccountAdminQuery();
				setDraft(undefined);
				setEditedPaths([]);
				setEditing(null);
				onEditModeChange?.(false);
				return;
			}
			// Nothing persisted — keep the card in edit mode with the draft intact so the user can retry.
			toast.error('Failed to save account changes. Please try again.');
		}
	};

	const isSaving = updateMutation.isPending || updateSpecialistMutation.isPending;
	const generalEditing = editing === 'general';
	const billingEditing = editing === 'billing';

	// A card is editable only while no other card is editing AND the admin holds ModifyAccount.
	const generalCanEdit = canModifyAccount && (editing === null || generalEditing);
	const billingCanEdit = canModifyAccount && (editing === null || billingEditing);

	const generalIsModifying = generalEditing && canModifyAccount;
	const billingIsModifying = billingEditing && canModifyAccount;

	// BillingCard consumes the `ICardEditMode` shape; adapt the shared parent state to it.
	const billingEdit: ICardEditMode<Api.IAccount> = {
		isEditMode: billingEditing,
		isModifyingAccount: billingIsModifying,
		draft: billingEditing ? draft : undefined,
		setDraftField,
		beginEdit: () => beginEdit('billing'),
		cancel,
		save: () => save(),
	};

	return (
		<div className={css(styles.column)}>
			<InternalOnly>
				<AccountInsightsBanner accountId={account.id} />
			</InternalOnly>

			<GeneralInformationCard
				account={account}
				isEditMode={generalEditing}
				isModifyingAccount={generalIsModifying}
				draft={generalEditing ? draft : undefined}
				setDraftField={setDraftField}
				canEdit={generalCanEdit}
				isSaving={isSaving && generalEditing}
				onEdit={() => beginEdit('general')}
				onSave={save}
				onCancel={cancel}
				managementAccountName={managementAccount?.companyName}
			/>

			{/* Legacy gated the whole Billing module to internal users; resellers must not see (let alone
				    edit) plan pricing, the billing reference id, or the renewal discount on their accounts. */}
			<InternalOnly>
				<BillingCard
					account={account}
					canEdit={billingCanEdit}
					isSaving={isSaving && billingEditing}
					onSave={save}
					edit={billingEdit}
					mainAccountName={mainAccount?.companyName}
				/>
			</InternalOnly>

			<InternalOnly>
				<CustomizationsCard account={account} />
			</InternalOnly>

			<AccountNotesSection account={account} />

			<SpecialistChangeJustificationDialog
				open={justificationDialogOpen}
				onOpenChange={open => {
					setJustificationDialogOpen(open);
					if (!open) {
						setSpecialistError(null);
					}
				}}
				companyName={account.companyName}
				previousSpecialistName={account.additionalInfo?.customerSuccessName}
				newSpecialistName={draft?.additionalInfo?.customerSuccessName}
				isSaving={isSaving}
				error={specialistError}
				onConfirm={justification => commitSave(justification)}
			/>
		</div>
	);
};
